import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Flame } from 'lucide-react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { SegmentedControl } from '../../../reusable';
import { money } from '../treasury/treasuryUi';

const CHART_BODY_HEIGHT = 260;
const PAD = { top: 16, right: 18, bottom: 44, left: 64 };
const CHANNEL_ORDER = ['B2B', 'B2G', 'B2B2C'];
const CHANNEL_COLORS = {
  B2B: '#2563eb',
  B2G: '#059669',
  B2B2C: '#d97706',
  'Sin canal': '#64748b',
};
const PRODUCT_COLORS = ['#0d9488', '#2563eb', '#d97706', '#7c3aed', '#db2777', '#0891b2', '#65a30d', '#ea580c'];
const STACK_ITEMS = [
  { key: 'channel', label: 'Canal' },
  { key: 'product', label: 'Producto' },
];
const METRIC_ITEMS = [
  { key: 'amount', label: '$' },
  { key: 'quantity', label: 'Galones' },
];

const zeros = (count) => Array.from({ length: Math.max(1, count) }, () => 0);

const addSeries = (left = [], right = [], count) => {
  const n = Math.max(count || 0, left.length, right.length, 1);
  return Array.from({ length: n }, (_, index) => Number(left[index] || 0) + Number(right[index] || 0));
};

const sumSeries = (series = []) => series.reduce((acc, value) => acc + Number(value || 0), 0);

const formatCompact = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toLocaleString('es-CL', { maximumFractionDigits: 1 })} mil MM`;
  if (abs >= 1e6) return `$${(n / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })} MM`;
  if (abs >= 1e5) return `$${(n / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 0 })} mil`;
  return money(n);
};

const formatQty = (value) =>
  Number(value || 0).toLocaleString('es-CL', { maximumFractionDigits: 1 });

const lerp = (start, end, t) => start + (end - start) * t;

const heatRgb = (t, isDarkMode) => {
  const eased = Math.max(0, Math.min(1, t)) ** 0.72;
  if (isDarkMode) {
    return [
      Math.round(lerp(22, 45, eased)),
      Math.round(lerp(78, 212, eased)),
      Math.round(lerp(80, 191, eased)),
    ];
  }
  return [
    Math.round(lerp(224, 13, eased)),
    Math.round(lerp(252, 116, eased)),
    Math.round(lerp(245, 110, eased)),
  ];
};

const heatTextColor = (rgb) => {
  const luminance = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  return luminance < 0.52 ? '#ffffff' : '#134e4a';
};

const heatRangeOf = (values) => {
  const positive = (values || []).map((value) => Number(value || 0)).filter((value) => value > 0);
  if (!positive.length) return { min: 0, max: 0 };
  return { min: Math.min(...positive), max: Math.max(...positive) };
};

const heatCellStyle = (value, range, { heatOn, isDarkMode, ui, strong = false }) => {
  const base = {
    padding: '7px 6px',
    textAlign: 'right',
    fontSize: 12,
    fontWeight: strong ? 700 : 500,
    color: ui.text,
    whiteSpace: 'nowrap',
    transition: 'background-color 120ms ease, color 120ms ease',
  };
  if (!heatOn) return base;
  const n = Number(value || 0);
  if (n <= 0 || !(range.max > 0)) {
    return {
      ...base,
      background: isDarkMode ? 'rgba(15, 23, 42, 0.2)' : '#f8fafc',
      color: ui.muted,
    };
  }
  const span = Math.max(range.max - range.min, 1e-9);
  const t = range.max <= range.min ? 1 : (n - range.min) / span;
  const rgb = heatRgb(t, isDarkMode);
  return {
    ...base,
    background: `rgb(${rgb.join(',')})`,
    color: heatTextColor(rgb),
    fontWeight: strong || t > 0.62 ? 700 : 600,
    boxShadow: `inset 0 0 0 1px ${isDarkMode ? 'rgba(15, 23, 42, 0.28)' : 'rgba(255, 255, 255, 0.55)'}`,
  };
};

const formatAxisMoney = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 0 })}k`;
  return `${sign}$${Math.round(abs).toLocaleString('es-CL')}`;
};

const formatAxisQty = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 1 })}k`;
  return `${sign}${Math.round(abs).toLocaleString('es-CL')}`;
};

const axisPointLabel = (index, monthAxis = []) => {
  const item = monthAxis[index];
  if (item?.month && item?.year) {
    const monthName = MONTH_LABELS[(Number(item.month) || 1) - 1] || item.short_label || item.label;
    const prev = monthAxis[index - 1];
    const showYear =
      index === 0 || Number(item.month) === 1 || (prev && Number(prev.year) !== Number(item.year));
    return {
      top: monthName,
      bottom: showYear ? String(item.year) : '',
      hover: item.label || `${monthName} ${item.year}`,
    };
  }
  const fallback = item?.label || item?.short_label || MONTH_LABELS[index] || String(index + 1);
  return { top: fallback, bottom: '', hover: fallback };
};

const usePlotSize = () => {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const apply = () => {
      const next = {
        width: Math.max(1, Math.floor(node.clientWidth)),
        height: Math.max(1, Math.floor(node.clientHeight)),
      };
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [wrapRef, size];
};

const ChartCard = ({ title, subtitle, value, ui, actions, children }) => (
  <div
    style={{
      background: ui.panel,
      border: `1px solid ${ui.border}`,
      borderRadius: 10,
      minWidth: 0,
      minHeight: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '7px 10px',
        borderBottom: `1px solid ${ui.border}`,
        background: ui.header,
        flexShrink: 0,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text, lineHeight: 1.2 }}>{title}</h3>
        <p style={{ margin: '2px 0 0', fontSize: 11, color: ui.muted }}>{subtitle}</p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {actions}
        {value ? <div style={{ fontSize: 13, fontWeight: 700, color: ui.text, whiteSpace: 'nowrap' }}>{value}</div> : null}
      </div>
    </div>
    <div style={{ flex: 1, minHeight: CHART_BODY_HEIGHT, position: 'relative', padding: '8px 12px 10px' }}>
      {children}
    </div>
  </div>
);

const buildSalesModel = (salesItems = [], monthCount = 12) => {
  const n = Math.max(1, Number(monthCount) || 12);
  const products = new Map();
  const channels = new Map();
  let amountMonths = zeros(n);
  let quantityMonths = zeros(n);

  (salesItems || []).forEach((item) => {
    const name = String(item.product_name || item.name || item.product_sku || 'Producto').trim() || 'Producto';
    const sku = String(item.product_sku || '').trim();
    const key = sku || name;
    const channel = String(item.channel || 'Sin canal').trim() || 'Sin canal';
    const months = Array.from({ length: n }, (_, index) => Number((item.months || [])[index] || 0));
    const qtys = Array.from({ length: n }, (_, index) => Number((item.quantity_months || [])[index] || 0));
    amountMonths = addSeries(amountMonths, months, n);
    quantityMonths = addSeries(quantityMonths, qtys, n);

    if (!products.has(key)) {
      products.set(key, { key, name, sku, amountMonths: zeros(n), quantityMonths: zeros(n), channels: new Map() });
    }
    const product = products.get(key);
    product.amountMonths = addSeries(product.amountMonths, months, n);
    product.quantityMonths = addSeries(product.quantityMonths, qtys, n);
    if (!product.channels.has(channel)) {
      product.channels.set(channel, { key: `${key}-${channel}`, name: channel, amountMonths: zeros(n), quantityMonths: zeros(n) });
    }
    const channelRow = product.channels.get(channel);
    channelRow.amountMonths = addSeries(channelRow.amountMonths, months, n);
    channelRow.quantityMonths = addSeries(channelRow.quantityMonths, qtys, n);

    if (!channels.has(channel)) {
      channels.set(channel, { key: channel, name: channel, amountMonths: zeros(n), quantityMonths: zeros(n) });
    }
    const channelTotal = channels.get(channel);
    channelTotal.amountMonths = addSeries(channelTotal.amountMonths, months, n);
    channelTotal.quantityMonths = addSeries(channelTotal.quantityMonths, qtys, n);
  });

  const sortChannels = (list) =>
    list.sort((a, b) => {
      const left = CHANNEL_ORDER.indexOf(a.name);
      const right = CHANNEL_ORDER.indexOf(b.name);
      return (left < 0 ? 99 : left) - (right < 0 ? 99 : right) || a.name.localeCompare(b.name);
    });

  const productRows = Array.from(products.values())
    .map((product) => ({
      ...product,
      amountTotal: sumSeries(product.amountMonths),
      quantityTotal: sumSeries(product.quantityMonths),
      children: sortChannels(Array.from(product.channels.values())).map((row) => ({
        ...row,
        amountTotal: sumSeries(row.amountMonths),
        quantityTotal: sumSeries(row.quantityMonths),
      })),
    }))
    .sort((a, b) => b.amountTotal - a.amountTotal || a.name.localeCompare(b.name));

  const channelRows = sortChannels(Array.from(channels.values())).map((row) => ({
    ...row,
    amountTotal: sumSeries(row.amountMonths),
    quantityTotal: sumSeries(row.quantityMonths),
  }));

  return {
    amountMonths,
    quantityMonths,
    amountTotal: sumSeries(amountMonths),
    quantityTotal: sumSeries(quantityMonths),
    products: productRows,
    channels: channelRows,
  };
};

const AreaLineChart = ({ series = [], extra = [], extraLabel = '', monthAxis = [], ui, isDarkMode }) => {
  const [hover, setHover] = useState(null);
  const [wrapRef, size] = usePlotSize();
  const width = size.width;
  const height = size.height;
  const plotW = Math.max(width - PAD.left - PAD.right, 1);
  const plotH = Math.max(height - PAD.top - PAD.bottom, 1);
  const points = useMemo(() => {
    const count = Math.max(series.length, extra.length, monthAxis.length, 1);
    return Array.from({ length: count }).map((_, index) => {
      const names = axisPointLabel(index, monthAxis);
      return {
        index,
        label: names.hover,
        topLabel: names.top,
        bottomLabel: names.bottom,
        value: Number(series[index] || 0),
        extra: Number(extra[index] || 0),
      };
    });
  }, [series, extra, monthAxis]);
  const values = points.map((point) => point.value);
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = Math.max(max - min, 1);
  const last = Math.max(points.length - 1, 1);
  const labelSize = points.length > 12 ? 9 : 11;
  const coords = points.map((point, index) => ({
    ...point,
    x: PAD.left + (plotW * index) / last,
    y: PAD.top + ((max - point.value) / span) * plotH,
  }));
  const line = coords.map((point) => `${point.x},${point.y}`).join(' ');
  const area = coords.length
    ? `${PAD.left},${PAD.top + ((max - 0) / span) * plotH} ${line} ${coords[coords.length - 1].x},${PAD.top + ((max - 0) / span) * plotH}`
    : '';
  const grid = isDarkMode ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.35)';
  const color = '#0d9488';
  const ticks = 4;

  const onMove = (event) => {
    if (!coords.length || !width) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    let nearest = coords[0];
    let best = Math.abs(coords[0].x - x);
    coords.forEach((point) => {
      const dist = Math.abs(point.x - x);
      if (dist < best) {
        best = dist;
        nearest = point;
      }
    });
    setHover(nearest);
  };

  if (!width || !height) {
    return <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }} />;
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
      >
        {Array.from({ length: ticks + 1 }).map((_, index) => {
          const value = max - (span * index) / ticks;
          const y = PAD.top + (plotH * index) / ticks;
          return (
            <g key={`tick-${index}`}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} stroke={grid} strokeWidth="1" />
              <text x={PAD.left - 8} y={y + 3} textAnchor="end" fill={ui.muted} fontSize="11">
                {formatAxisMoney(value)}
              </text>
            </g>
          );
        })}
        {coords.length ? (
          <>
            <polygon points={area} fill={color} opacity="0.12" />
            <polyline points={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
          </>
        ) : null}
        {coords.map((point) => (
          <g key={`label-${point.index}`}>
            <text x={point.x} y={height - (point.bottomLabel ? 18 : 6)} textAnchor="middle" fill={ui.muted} fontSize={labelSize}>
              {point.topLabel}
            </text>
            {point.bottomLabel ? (
              <text x={point.x} y={height - 6} textAnchor="middle" fill={ui.muted} fontSize={labelSize}>
                {point.bottomLabel}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
      {hover ? (
        <div
          style={{
            position: 'absolute',
            left: `${Math.min(82, Math.max(8, (hover.x / width) * 100))}%`,
            top: 8,
            transform: 'translateX(-50%)',
            background: ui.panel,
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            padding: '8px 10px',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            pointerEvents: 'none',
            minWidth: 160,
            zIndex: 2,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted }}>{hover.label}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color, marginTop: 4 }}>{money(hover.value)}</div>
          {extraLabel ? (
            <div style={{ fontSize: 12, color: ui.muted, marginTop: 2 }}>
              {extraLabel} {formatQty(hover.extra)}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const StackedBarChart = ({
  series = [],
  monthAxis = [],
  ui,
  isDarkMode,
  formatTick = formatAxisMoney,
  formatValue = money,
}) => {
  const [hover, setHover] = useState(null);
  const [wrapRef, size] = usePlotSize();
  const width = size.width;
  const height = size.height;
  const plotW = Math.max(width - PAD.left - PAD.right, 1);
  const plotH = Math.max(height - PAD.top - PAD.bottom, 1);
  const count = Math.max(monthAxis.length, ...(series.map((item) => (item.months || []).length)), 1);
  const points = useMemo(
    () =>
      Array.from({ length: count }).map((_, index) => {
        const names = axisPointLabel(index, monthAxis);
        const parts = series.map((item) => ({
          key: item.key,
          name: item.name,
          color: item.color,
          value: Number((item.months || [])[index] || 0),
        }));
        return {
          index,
          label: names.hover,
          topLabel: names.top,
          bottomLabel: names.bottom,
          total: parts.reduce((acc, part) => acc + part.value, 0),
          parts,
        };
      }),
    [count, monthAxis, series]
  );
  const labelSize = points.length > 12 ? 9 : 11;
  const max = Math.max(0, ...points.map((point) => point.total));
  const span = Math.max(max, 1);
  const slot = plotW / Math.max(points.length, 1);
  const barW = Math.max(Math.min(slot * 0.62, 28), 6);
  const grid = isDarkMode ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.35)';
  const ticks = 4;
  const yFor = (value) => PAD.top + ((max - value) / span) * plotH;

  if (!width || !height) {
    return <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }} />;
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        {Array.from({ length: ticks + 1 }).map((_, index) => {
          const value = max - (span * index) / ticks;
          const y = PAD.top + (plotH * index) / ticks;
          return (
            <g key={`tick-${index}`}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} stroke={grid} strokeWidth="1" />
              <text x={PAD.left - 8} y={y + 3} textAnchor="end" fill={ui.muted} fontSize="11">
                {formatTick(value)}
              </text>
            </g>
          );
        })}
        {points.map((point) => {
          const cx = PAD.left + slot * point.index + slot / 2;
          let cursor = 0;
          return (
            <g
              key={point.index}
              onMouseEnter={() => setHover({ ...point, x: cx })}
              onMouseLeave={() => setHover(null)}
            >
              {point.parts.map((part) => {
                if (part.value <= 0) return null;
                const top = cursor + part.value;
                const y = yFor(top);
                const h = Math.max(yFor(cursor) - y, 1);
                cursor = top;
                return <rect key={part.key} x={cx - barW / 2} y={y} width={barW} height={h} fill={part.color} rx="2" />;
              })}
              <text x={cx} y={height - (point.bottomLabel ? 18 : 6)} textAnchor="middle" fill={ui.muted} fontSize={labelSize}>
                {point.topLabel}
              </text>
              {point.bottomLabel ? (
                <text x={cx} y={height - 6} textAnchor="middle" fill={ui.muted} fontSize={labelSize}>
                  {point.bottomLabel}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {hover ? (
        <div
          style={{
            position: 'absolute',
            left: `${Math.min(82, Math.max(8, (hover.x / width) * 100))}%`,
            top: 8,
            transform: 'translateX(-50%)',
            background: ui.panel,
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            padding: '8px 10px',
            pointerEvents: 'none',
            minWidth: 170,
            zIndex: 2,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted }}>{hover.label}</div>
          {hover.parts
            .filter((part) => Math.abs(part.value) > 0.004)
            .map((part) => (
              <div key={part.key} style={{ fontSize: 12, fontWeight: 700, color: part.color, marginTop: 3 }}>
                {part.name} {formatValue(part.value)}
              </div>
            ))}
          <div style={{ fontSize: 12, fontWeight: 700, color: ui.text, marginTop: 6 }}>Total {formatValue(hover.total)}</div>
        </div>
      ) : null}
    </div>
  );
};

const BudgetSalesOverview = ({
  budget = null,
  monthAxis = [],
  ui,
  isDarkMode = false,
  loading = false,
}) => {
  const [stackMode, setStackMode] = useState('channel');
  const [mixMetric, setMixMetric] = useState('amount');
  const [metric, setMetric] = useState('amount');
  const [heatOn, setHeatOn] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const monthCount = Math.max(1, monthAxis.length || Number(budget?.month_count) || 12);
  const model = useMemo(
    () => buildSalesModel(budget?.sales_items || [], monthCount),
    [budget, monthCount]
  );
  const headers = (monthAxis || []).map((item) => item?.label || item?.short_label || `${item?.month || ''}/${item?.year || ''}`);
  const cumulativeAmount = useMemo(() => {
    let running = 0;
    return model.amountMonths.map((value) => {
      running += Number(value || 0);
      return running;
    });
  }, [model]);
  const cumulativeQty = useMemo(() => {
    let running = 0;
    return model.quantityMonths.map((value) => {
      running += Number(value || 0);
      return running;
    });
  }, [model]);
  const stackSeries = useMemo(() => {
    const source = stackMode === 'product' ? model.products : model.channels;
    return source.map((row, index) => ({
      key: row.key,
      name: row.name,
      color:
        stackMode === 'channel'
          ? CHANNEL_COLORS[row.name] || PRODUCT_COLORS[index % PRODUCT_COLORS.length]
          : PRODUCT_COLORS[index % PRODUCT_COLORS.length],
      months: mixMetric === 'quantity' ? row.quantityMonths : row.amountMonths,
    }));
  }, [model, stackMode, mixMetric]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const th = {
    textAlign: 'right',
    padding: '8px 6px',
    borderBottom: `1px solid ${ui.border}`,
    fontSize: 11,
    color: ui.muted,
    whiteSpace: 'nowrap',
    background: ui.header,
  };

  const cellValue = (row, index) =>
    metric === 'quantity'
      ? Number((row.quantityMonths || [])[index] || 0)
      : Number((row.amountMonths || [])[index] || 0);
  const rowTotal = (row) =>
    metric === 'quantity' ? Number(row.quantityTotal || 0) : Number(row.amountTotal || 0);
  const formatCell = (row, index) => (metric === 'quantity' ? formatQty(cellValue(row, index)) : money(cellValue(row, index)));
  const formatTotal = (row) => (metric === 'quantity' ? formatQty(rowTotal(row)) : money(rowTotal(row)));
  const heatRange = useMemo(() => {
    const months = [];
    const totals = [];
    (model.products || []).forEach((product) => {
      const monthSeries = metric === 'quantity' ? product.quantityMonths : product.amountMonths;
      months.push(...(monthSeries || []));
      totals.push(metric === 'quantity' ? product.quantityTotal : product.amountTotal);
      (product.children || []).forEach((child) => {
        const childSeries = metric === 'quantity' ? child.quantityMonths : child.amountMonths;
        months.push(...(childSeries || []));
        totals.push(metric === 'quantity' ? child.quantityTotal : child.amountTotal);
      });
    });
    return { months: heatRangeOf(months), totals: heatRangeOf(totals) };
  }, [model, metric]);
  const heatOpts = { heatOn, isDarkMode, ui };

  if (loading) {
    return <p style={{ margin: 0, padding: '24px 12px', fontSize: 13, color: ui.muted }}>Calculando…</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 12,
          alignItems: 'stretch',
        }}
      >
        <ChartCard
          title="Ventas acumuladas"
          subtitle={`${formatQty(model.quantityTotal)} galones proyectados`}
          value={formatCompact(model.amountTotal)}
          ui={ui}
        >
          <AreaLineChart
            series={cumulativeAmount}
            extra={cumulativeQty}
            extraLabel="Galones"
            monthAxis={monthAxis}
            ui={ui}
            isDarkMode={isDarkMode}
          />
        </ChartCard>
        <ChartCard
          title="Mix mensual"
          subtitle={stackMode === 'channel' ? 'Apilado por canal' : 'Apilado por producto'}
          value={
            mixMetric === 'quantity'
              ? `${formatQty(model.quantityTotal)} gal`
              : formatCompact(model.amountTotal)
          }
          ui={ui}
          actions={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <SegmentedControl ariaLabel="Agrupación del mix" items={STACK_ITEMS} activeKey={stackMode} onChange={setStackMode} />
              <SegmentedControl ariaLabel="Métrica del mix" items={METRIC_ITEMS} activeKey={mixMetric} onChange={setMixMetric} />
            </div>
          }
        >
          <StackedBarChart
            series={stackSeries}
            monthAxis={monthAxis}
            ui={ui}
            isDarkMode={isDarkMode}
            formatTick={mixMetric === 'quantity' ? formatAxisQty : formatAxisMoney}
            formatValue={mixMetric === 'quantity' ? formatQty : money}
          />
        </ChartCard>
      </div>
      <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div
          style={{
            padding: '10px 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text }}>Ventas por producto</h3>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: ui.muted }}>
              Expandir un producto para ver el desglose por canal.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              aria-pressed={heatOn}
              onClick={() => setHeatOn((prev) => !prev)}
              style={{
                height: 32,
                padding: '0 12px',
                borderRadius: 999,
                border: `1px solid ${heatOn ? ui.interactive || '#0d9488' : ui.border}`,
                background: heatOn ? ui.interactive || '#0d9488' : 'transparent',
                color: heatOn ? ui.textOnAccent || '#fff' : ui.text,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
              }}
            >
              <Flame size={14} />
              Mapa de calor
            </button>
            <SegmentedControl ariaLabel="Métrica de ventas" items={METRIC_ITEMS} activeKey={metric} onChange={setMetric} />
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 240 + headers.length * 92 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', minWidth: 220 }}>Producto / Canal</th>
                {headers.map((label, index) => (
                  <th key={`${label}-${index}`} style={th}>
                    {label}
                  </th>
                ))}
                <th style={th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {model.products.map((product) => {
                const open = expanded.has(product.key);
                const rows = [{ ...product, level: 0, canExpand: product.children.length > 0, open }];
                if (open) {
                  product.children.forEach((child) => rows.push({ ...child, level: 1, canExpand: false, open: false }));
                }
                return (
                  <Fragment key={product.key}>
                    {rows.map((row) => (
                  <tr key={row.key}>
                    <td style={{ padding: '7px 8px', position: 'sticky', left: 0, background: ui.panel }}>
                      <button
                        type="button"
                        onClick={() => row.canExpand && toggle(row.key)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          background: 'transparent',
                          color: ui.text,
                          cursor: row.canExpand ? 'pointer' : 'default',
                          paddingLeft: 4 + row.level * 16,
                          fontSize: 12,
                          fontWeight: row.level === 0 ? 700 : 500,
                        }}
                      >
                        {row.canExpand ? (
                          row.open ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                        ) : (
                          <span style={{ width: 14 }} />
                        )}
                        {row.name}
                      </button>
                    </td>
                    {headers.map((_, index) => (
                      <td
                        key={`${row.key}-m${index}`}
                        style={heatCellStyle(cellValue(row, index), heatRange.months, heatOpts)}
                      >
                        {formatCell(row, index)}
                      </td>
                    ))}
                    <td style={heatCellStyle(rowTotal(row), heatRange.totals, { ...heatOpts, strong: true })}>
                      {formatTotal(row)}
                    </td>
                  </tr>
                    ))}
                  </Fragment>
                );
              })}
              {!model.products.length ? (
                <tr>
                  <td colSpan={headers.length + 2} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                    Aún no hay ventas proyectadas en la estructura de ingresos.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BudgetSalesOverview;
